import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'

import Box from '@components/kit/box'
import Card from '@components/kit/card'
import { Column, Row } from '@components/kit/grid'
import Loading from '@components/kit/loading'
import Text from '@components/kit/text'

import { ChatContext } from '../chat.web'
import { useHealthTrackingStore } from '@store/HealthTracking'
import { IHealthTrackingWeight } from '@store/HealthTracking/types'
import Pressable from '@components/kit/pressable'
import { ChevronDownIcon } from '@components/kit/icons'

const PAGE_SIZE = 30
const RETRACTED_PAGE_SIZE = 10

function WeightLog() {
  const { userId } = useContext(ChatContext)
  const { getWeightLog } = useHealthTrackingStore.getState()
  const [weightLog, setWeightLog] = useState<IHealthTrackingWeight | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [currentPageSize, setCurrentPageSize] =
    useState<number>(RETRACTED_PAGE_SIZE)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const isRetractable = (weightLog?.results?.length ?? 0) > RETRACTED_PAGE_SIZE

  const toggleExpanded = () => {
    if (isExpanded) {
      setCurrentPageSize(RETRACTED_PAGE_SIZE)
    } else {
      setCurrentPageSize(PAGE_SIZE)
    }
    setIsExpanded(!isExpanded)
  }

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      const data = await getWeightLog(userId, currentPage, PAGE_SIZE)
      setWeightLog(data)
      setIsLoading(false)
    }
    fetchData()
  }, [userId, currentPage])

  return (
    <React.Fragment>
      <Row
        mt='24'
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
      >
        <Text ml='12' mb='08' font='display.16.bold'>
          Weight Log
        </Text>

        {isRetractable && (
          <Pressable
            mr='12'
            onPress={toggleExpanded}
            width='20'
            height='20'
            justifyContent='center'
            alignItems='center'
            style={{ transform: [{ rotate: !isExpanded ? '0deg' : '180deg' }] }}
          >
            <ChevronDownIcon fill='shade07' width={20} height={20} />
          </Pressable>
        )}
      </Row>
      <Card mt='08' px='00' py='00'>
        {isLoading ? (
          <Box pt='24'>
            <Loading mt='20' py='20' />
          </Box>
        ) : (
          <React.Fragment>
            <Row m='16'>
              <Column width='25%'>
                <Text font='text.12.bold'>Date</Text>
              </Column>
              <Column width='25%'>
                <Text font='text.12.bold'>lbs</Text>
              </Column>
              <Column width='25%'>
                <Text font='text.12.bold'>kg</Text>
              </Column>
              <Column width='25%'>
                <Text font='text.12.bold'>Source</Text>
              </Column>
            </Row>

            {weightLog?.results.length === 0 && (
              <Row mx='16' my='12'>
                <Text font='text.14.medium'>No data available yet.</Text>
              </Row>
            )}

            {weightLog?.results.map((weight, index) => {
              if (index >= currentPageSize) {
                return null
              }
              const isRecent =
                moment().diff(moment(weight.timestamp), 'days') <= 7
              return (
                <Row key={index} mx='16' my='12'>
                  <Column width='25%' flexDirection='row' alignItems='center'>
                    <Text font='text.14.medium'>
                      {moment(weight.timestamp, 'YYYY-MM-DDTHH:mm:ssZ').format(
                        'MM/DD/YY'
                      )}
                    </Text>
                  </Column>
                  <Column width='25%' flexDirection='row' alignItems='center'>
                    {isRecent && (
                      <Box
                        backgroundColor='walrusBlue500'
                        width='12px'
                        height='12px'
                        mr='08'
                        borderRadius='xlarge'
                      />
                    )}
                    <Text font='text.14.medium'>
                      {weight.weight_pounds.toLocaleString('en-US')}
                    </Text>
                  </Column>
                  <Column width='25%' flexDirection='row' alignItems='center'>
                    <Text font='text.14.medium'>
                      {weight.weight_kilograms.toLocaleString('en-US')}
                    </Text>
                  </Column>
                  <Column width='25%' flexDirection='row' alignItems='center'>
                    <Text font='text.14.medium'>{weight.source}</Text>
                  </Column>
                </Row>
              )
            })}
          </React.Fragment>
        )}
      </Card>
    </React.Fragment>
  )
}

export default WeightLog
